import { produce } from "immer";
import { getEffectiveTabMode, getNonZeroData, getRowDataSortedAndRanked, persistedMetricsModules, tabIndexes } from "../../agent-production/helpers/agentProductionHelpers";
import { agentProductionTerms as apt, modules } from "../../constants";
import { filterIds, searchConfigByTabId, searchConfigFallback, tableSettingsByTab, tabs } from "../../agent-production/constants/suggestedAgents";
import { getPropertyTypes } from "../../service/mlsInfo";
import { getPropertyTypeValues, getSearchCriteriaPropertyTypes } from "../../agent-production/components/savedAgents/helpers";
import { buildSearchCriteria } from "../../common/helpers/utilities";
import { useSearchStore, useCommonStore } from "../store";
import { getCurrentTabState } from "../../agent-production/components/suggestedAgents/helpers/helpers";
import { useSearchResultsStore } from '../searchResults/store';
import { agentsColumns, viewHideAgentsDropdownLabels } from "../../agent-production/constants/agentProductionConstants";
import { initialSearchState, initialView } from "../search/store";
import { views } from "../../agent-production/constants/savedAgents";

const suggestedAgentsActions = (set, get) => ({
    setSearchFilterState: (value, filterId, stateId, hasRefreshSearchBtn) => set(produce(state => {
        const currentModule = get().selectedModule;
        const tabState = getCurrentTabState(currentModule, state);
        if (currentModule !== modules.proficiencyMetrics.suggestedAgents) return;
        if (filterId !== filterIds.display) {
            tabState[filterId] = { ...tabState[filterId], ...value };
            return;
        }
        if (filterId === filterIds.producers && hasRefreshSearchBtn) {
            tabState.producers = { ...tabState.producers, ...value };
            return;
        };
        tabState[filterId][stateId] = value;
    }, false, 'setSearchFilterState')),
    getCurrentTabSearchOptions: () => {
        const searchStoreState = useSearchStore.getState();
        const currentTabId = searchStoreState[searchStoreState.selectedModule]?.currentTabId;
        
        if (currentTabId === tabs.todaysPicks.id) {
            return { displayOptions: null, producerOptions: null };
        }
        
        const {
            [currentTabId]: currentTabSearchOptions
        } = searchStoreState[searchStoreState.selectedModule].searchOptions;
        const {display: displayOptions, producers: producerOptions} = currentTabSearchOptions;
        return { displayOptions, producerOptions };
    },
    setSuggestedAgentsData: async ({ setState, mlsId, currentTabId }) => {
        if (currentTabId === tabs.todaysPicks.id) {
            get().setIsLoading(false);
            return;
        }
        try {
            get().setIsLoading(true);
            const shouldNotRank = false;
            const isCoopAgentsOverview = (currentTabId === tabs.coopAgents.id);
            const officeIds = isCoopAgentsOverview
                ? useCommonStore.getState().privileges?.userOffices.find(o => o.mlsId === mlsId)?.officeIds
                : [...new Set(useCommonStore.getState().privileges?.userOffices.flatMap(obj => obj.officeIds))];
            const propertyTypes = await getPropertyTypes(mlsId);
            const { producerOptions, displayOptions } = get().getCurrentTabSearchOptions();
            const { getApiService, getParameters, getProducerValues } = searchConfigByTabId[currentTabId] || searchConfigFallback;
            const isGrowthAnalysis = displayOptions.selectedView.value === views.growthAnalysis;
            const currentTabProducer = getProducerValues(producerOptions, isGrowthAnalysis);
            const timePeriod = currentTabProducer?.newProd || currentTabProducer?.timePeriod || apt.annually;
            const allResidentialPropertyTypes = getSearchCriteriaPropertyTypes(propertyTypes);
            const propertyTypeNames = getPropertyTypeValues(allResidentialPropertyTypes, 'longName');
            const parameters = getParameters && getParameters(currentTabProducer);
            const searchCriteria = buildSearchCriteria({
                currentTabId,
                mlsId,
                officeIds,
                propertyTypeIds: getPropertyTypeValues(allResidentialPropertyTypes, 'typeId'),
                propertyTypeNames,
                searchAllMLS: false,
                timePeriod,
            });

            const { recruitingSoldResponse, recruitingSoldTotals } = await getApiService(searchCriteria, parameters);
            const tabConfig = tableSettingsByTab[currentTabId];
            const rowData = tabConfig?.shouldExcludeZeroGrowthAgents
                ? getNonZeroData(recruitingSoldResponse, agentsColumns.totalVolumeGrowth.id)
                : getRowDataSortedAndRanked(recruitingSoldResponse, shouldNotRank, isCoopAgentsOverview);
            get().setSearchCriteria(searchCriteria);
            get().setLoadedSearch();
            const searchResultsStore = useSearchResultsStore.getState();
            searchResultsStore.setSearchResults({results: recruitingSoldResponse, totals: recruitingSoldTotals})
            setState(state => ({
                ...state,
                rowDataByTab: {
                    ...state.rowDataByTab,
                    [currentTabId]: { results: rowData.map(d => ({...d, hasFavStar: true})), totals: recruitingSoldTotals }
                },
                propertyTypeNames,
                timePeriod,
            }));
        } catch (err) {
            console.error('Error retrieving Suggested Agents data: ', err);
        } finally {
            get().setIsLoading(false);
        }
    },
    setCurrentTabIndex: (currentTabIndex, tabValues) => set(produce(state => {
        const moduleState = state[get().selectedModule];
        if (moduleState?.currentTabId === undefined) return;
        moduleState.currentTabIndex = currentTabIndex;
        moduleState.currentTabId = tabValues?.[currentTabIndex]?.id;
    }), false, 'setSuggestedAgentsCurrentTab'),    
    setSelectedRowIndex: (selectedRowIndex) => set(produce(state => {
        const moduleState = state[get().selectedModule];
        moduleState.selectedRowIndex = selectedRowIndex;
    }), false, 'setSelectedRowIndex'),
    setTabDescription: value => set(produce(state => {
        const currentModule = get().selectedModule;
        const tabState = getCurrentTabState(currentModule, state);
        tabState.tabDescription = value;
    }), false, 'setTabDescription'),
    resetFiltersState: () => set(produce(state => {
        const currentModule = get().selectedModule;
        if (currentModule !== modules.proficiencyMetrics.suggestedAgents) return;
        state[currentModule] = state[currentModule] = {
            ...initialSearchState(initialView)[currentModule],
            currentTabId: state[currentModule]?.currentTabId,
            currentTabIndex: state[currentModule]?.currentTabIndex,
            selectedMls: state[currentModule]?.selectedMls,
        };
    }), false, 'resetFiltersState'),
});

export const actions = (set, get) => ({
    ...suggestedAgentsActions(set, get),
    setCurrentTab: currentTab => set(produce(state => {
        const moduleState = state[get().selectedModule];
        if (moduleState?.currentTab === undefined) return;
        moduleState.currentTab = currentTab;
    }), false, 'setProfMetricsCurrentTab'),
    resetViewHideAgentsFilter: () => set(produce(state => {
        const moduleState = state[get().selectedModule];
        if (!moduleState) return;
        moduleState.selectedViewHideAgentsFilter = viewHideAgentsDropdownLabels.viewHideAgents;
    }), false, "resetViewHideAgentsFilter"),
    resetPMPersistedData: () => {
        get().setCurrentTab(tabIndexes.OVERVIEW);
        get().resetViewHideAgentsFilter();
    },
    setViewHideAgentsFilter: (module, value) =>
        set(produce(state => {
            const moduleState = state[module];
            if (moduleState?.selectedViewHideAgentsFilter !== undefined) {
                moduleState.selectedViewHideAgentsFilter = value;
            }
        }), false, "setViewHideAgentsFilter"),
    setPMSelectedColumnIds: (updatedColumns, tabMode, isPeriodLast12Months) => set(produce(state => {
        const selectedModule = get().selectedModule;
        if (!persistedMetricsModules.includes(selectedModule)) return;
        const effectiveTabMode = getEffectiveTabMode(tabMode, isPeriodLast12Months)
        const moduleState = state[selectedModule];

        if (!moduleState.selectedColumnIds) {
            moduleState.selectedColumnIds = {};
        }
        if (!moduleState.selectedColumnIds?.[effectiveTabMode]) {
            moduleState.selectedColumnIds[effectiveTabMode] = {};
        }
        moduleState.selectedColumnIds[effectiveTabMode] = {
            columns: updatedColumns ?? []
        };
    }), false, 'setPMSelectedColumnIds'),
    clearPMSelectedColumnIds: () => set(produce(state => {
        const selectedModule = get().selectedModule;
        const moduleState = state[selectedModule];
        if (!moduleState) return;
        moduleState.selectedColumnIds = {};
    }), false, 'clearPMSelectedColumnIds'),
});
